import React, { useRef } from "react";
import { Divider, IconButton, Menu } from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import MoreVertIcon from "@mui/icons-material/MoreVert";

import { useTranslation } from "@/lib/i18n";
import {
  NavigationElementSettings,
  RemoveFolderConfirmDialog,
  useDeleteNavigationElementMutation,
} from "@/features/admin/features/pages-management";
import { MenuItem } from "@/features/admin/components";
import { ModalComponent, confirm, showToast } from "@/features/shared/components";
import { NavigationElement } from "@/lib/api";

type Props = {
  navigationElement: NavigationElement;
};

const EditOverviewItemMenu = ({ navigationElement }: Props) => {
  const { t } = useTranslation();
  const deleteNavigationElementMutation = useDeleteNavigationElementMutation();

  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);

  const settingsModalRef = useRef<ModalComponent>();

  const open = Boolean(anchorEl);
  const isFolder = !navigationElement.page_id;

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleRemovePage = async () => {
    const result = await confirm(
      t("admin.pages.remove_page_confirm.title"),
      t("admin.pages.remove_page_confirm.body"),
    );

    if (result) {
      deleteNavigationElementMutation.mutate(navigationElement.id, {
        onSuccess: () => {
          showToast(t("admin.messages.page_deleted"));
        },
      });
    }
  };

  return (
    <>
      <IconButton onClick={handleClick}>
        <MoreVertIcon />
      </IconButton>
      <Menu anchorEl={anchorEl} open={open} onClose={handleClose}>
        <MenuItem
          icon={<EditIcon fontSize="small" />}
          title={t("admin.pages.overview.page_menu.edit")}
          onClick={() => settingsModalRef.current?.open?.()}
        />

        <Divider key="divider" />
        {isFolder ? (
          <RemoveFolderConfirmDialog
            onClose={handleClose}
            navigationElement={navigationElement}
            triggerElement={
              <MenuItem
                icon={<DeleteIcon fontSize="small" />}
                title={t("admin.pages.overview.page_menu.remove")}
              />
            }
          />
        ) : (
          <MenuItem
            onClick={handleRemovePage}
            icon={<DeleteIcon fontSize="small" />}
            title={t("admin.pages.overview.page_menu.remove")}
          />
        )}
      </Menu>
      <NavigationElementSettings
        ref={settingsModalRef}
        initialValues={navigationElement}
        onClose={handleClose}
      />
    </>
  );
};

export default EditOverviewItemMenu;
